import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';

import type { ActivityEventType, HistoryRangePreset } from '../types';

import styles from './history-filter-drawer.module.scss';

/** Concrete, render-safe filter (from/to are resolved at Apply time). */
export type ActivityFilter = {
  types: ActivityEventType[];
  from: string | null;
  to: string | null;
};

export const EMPTY_ACTIVITY_FILTER: ActivityFilter = { types: [], from: null, to: null };

const TYPE_OPTIONS: Array<{ value: ActivityEventType; label: string }> = [
  { value: 'command', label: 'Commands' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'alert', label: 'Alerts' },
];

const PRESETS: Array<{ value: HistoryRangePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom' },
];

export type HistoryFilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  onApply: (filter: ActivityFilter) => void;
};

export function HistoryFilterDrawer({ open, onClose, onApply }: HistoryFilterDrawerProps) {
  const [types, setTypes] = useState<ActivityEventType[]>([]);
  const [preset, setPreset] = useState<HistoryRangePreset>('7d');
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);

  const toggleType = (value: ActivityEventType) =>
    setTypes((t) => (t.includes(value) ? t.filter((x) => x !== value) : [...t, value]));

  const apply = () => {
    const now = dayjs();
    let from: string | null = null;
    let to: string | null = now.toISOString();
    if (preset === 'today') from = now.startOf('day').toISOString();
    else if (preset === '7d') from = now.subtract(7, 'day').toISOString();
    else if (preset === '30d') from = now.subtract(30, 'day').toISOString();
    else if (preset === 'custom' && custom) {
      from = custom[0].startOf('day').toISOString();
      to = custom[1].endOf('day').toISOString();
    } else if (preset === 'custom') {
      from = null;
      to = null;
    }
    onApply({ types, from, to });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      primaryVariant="neutral"
      onPrimary={apply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <div className={styles.body}>
        <fieldset className={styles.group}>
          <legend className={styles.legend}>Event Type</legend>
          <div className={styles.chips}>
            {TYPE_OPTIONS.map((o) => {
              const active = types.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={active}
                  className={[styles.chip, active ? styles.active : undefined]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => toggleType(o.value)}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className={styles.group}>
          <legend className={styles.legend}>Date Range</legend>
          <div className={styles.chips}>
            {PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={preset === p.value}
                className={[styles.chip, preset === p.value ? styles.active : undefined]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setPreset(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' ? (
            <div className={styles.calendar}>
              <DateRangePicker
                value={custom}
                onChange={(v) => setCustom(v as [Dayjs, Dayjs] | null)}
              />
            </div>
          ) : null}
        </fieldset>
      </div>
    </Drawer>
  );
}
